import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Paper,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
  PriceCheck as CheckIcon,
  WarningAmber as WarningIcon,
  ShoppingCart as CartIcon
} from '@mui/icons-material';

// Denominaciones comunes para accesos rápidos
const DENOMINACIONES = [5, 10, 20, 50, 100];

export const CalculadoraCambioModal = ({
  open,
  onClose,
  totalPagar = 0,
  onConfirmarPago // Callback opcional por si deseas confirmar la transacción desde aquí
}) => {
  const [totalAPagar, setTotalAPagar] = useState('');
  const [efectivoRecibido, setEfectivoRecibido] = useState('');

  // Inicializar / resetear valores cada vez que abre el modal o cambia el total del prop
  useEffect(() => {
    if (open) {
      const totalInicial = parseFloat(totalPagar) || 0;
      setTotalAPagar(totalInicial > 0 ? totalInicial.toFixed(2) : '');
      setEfectivoRecibido('');
    }
  }, [open, totalPagar]);

  const total = parseFloat(totalAPagar) || 0;
  const recibido = parseFloat(efectivoRecibido) || 0;
  const diferencia = recibido - total;

  const esSuficiente = recibido >= total && total > 0;
  const esCero = recibido === 0 || total === 0;
  const hayValores = total > 0 || recibido > 0;

  // Manejadores para billetes rápidos
  const handleBilletesRapidos = (monto) => {
    setEfectivoRecibido(monto.toString());
  };

  const handleMontoExacto = () => {
    if (total > 0) {
      setEfectivoRecibido(total.toFixed(2));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalculateIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Calculadora de Cambio
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* Input Total a Pagar (ahora editable) */}
          <TextField
            fullWidth
            type="number"
            label="Total a Pagar"
            placeholder="0.00"
            value={totalAPagar}
            onChange={(e) => setTotalAPagar(e.target.value)}
            inputProps={{ step: '0.01', min: '0', style: { fontSize: '1.4rem', fontWeight: 'bold' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CartIcon color="primary" />
                </InputAdornment>
              ),
            }}
            helperText={totalPagar > 0 ? 'Puedes modificar el total si lo necesitas' : 'Ingresa el monto a cobrar'}
          />

          {/* Input Efectivo Recibido */}
          <TextField
            fullWidth
            autoFocus={!(totalPagar > 0)}
            type="number"
            label="Efectivo Recibido"
            placeholder="0.00"
            value={efectivoRecibido}
            onChange={(e) => setEfectivoRecibido(e.target.value)}
            inputProps={{ step: '0.01', min: '0', style: { fontSize: '1.4rem', fontWeight: 'bold' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MoneyIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Botones de Denominación Rápida */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Billetes / Montos Rápidos:
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={handleMontoExacto}
                  disabled={total <= 0}
                  sx={{ fontWeight: 'bold' }}
                >
                  Exacto
                </Button>
              </Grid>
              {DENOMINACIONES.map((monto) => (
                <Grid item xs={4} key={monto}>
                  <Button
                    fullWidth
                    size="small"
                    variant={recibido === monto ? 'contained' : 'outlined'}
                    onClick={() => handleBilletesRapidos(monto)}
                  >
                    ${monto}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider />

          {/* Visualización del Resultado (Cambio o Faltante) */}
          {hayValores && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                textAlign: 'center',
                borderRadius: 2,
                bgcolor: !total
                  ? '#f8fafc'
                  : esSuficiente
                    ? '#f0fdf4'
                    : '#fef2f2',
                border: '1px solid',
                borderColor: !total
                  ? '#e2e8f0'
                  : esSuficiente
                    ? '#bbf7d0'
                    : '#fecaca',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {!total ? null : esSuficiente ? (
                  <CheckIcon color="success" />
                ) : (
                  <WarningIcon color="error" />
                )}
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  sx={{
                    color: !total
                      ? 'text.secondary'
                      : esSuficiente
                        ? 'success.dark'
                        : 'error.dark',
                  }}
                >
                  {!total
                    ? 'INGRESA EL TOTAL A PAGAR'
                    : esSuficiente
                      ? 'CAMBIO A ENTREGAR'
                      : 'EFECTIVO INSUFICIENTE'}
                </Typography>
              </Box>

              {total > 0 && (
                <>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    sx={{ color: esSuficiente ? 'success.main' : 'error.main' }}
                  >
                    ${Math.abs(diferencia).toFixed(2)}
                  </Typography>

                  {!esSuficiente && recibido > 0 && (
                    <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                      Faltan ${Math.abs(diferencia).toFixed(2)} para completar el pago.
                    </Typography>
                  )}
                </>
              )}
            </Paper>
          )}

        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cerrar
        </Button>
        {onConfirmarPago && (
          <Button
            variant="contained"
            color="success"
            disabled={!esSuficiente || esCero}
            onClick={() => onConfirmarPago(recibido, diferencia)}
          >
            Confirmar Pago
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CalculadoraCambioModal;